import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import Drawer, {
  DrawerHeader,
  DrawerTitle,
  DrawerBody,
  DrawerFooter,
  DrawerCloseTrigger,
} from "@/components/ui/Drawer";
import {
  FormErrorDisplay,
  RootErrorDisplay,
} from "@/components/ui/FormErrorDisplay";
import Select from "@/components/ui/Select";
import DateInput from "@/components/ui/DateInput";
import withHashDrawer from "@/components/ui/withHashDrawer";
import {
  OrganizationStatus,
  ORGANIZATION_STATUS_OPTIONS,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationQuery,
} from "../api";
import { useGetPaymentProvidersQuery } from "@entities/payment-providers/api";
import { useFormErrorHandler } from "@/hooks/useFormErrorHandler";

// Schema definition
const organizationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Organization name is required")
    .min(2, "Name must be at least 2 characters long")
    .max(100, "Name must be less than 100 characters"),
  status: yup
    .string()
    .required("Status is required")
    .oneOf(
      Object.values(OrganizationStatus),
      "Invalid status selected"
    ) as yup.Schema<OrganizationStatus>,
  payment_provider: yup
    .string()
    .optional()
    .transform((value) => value === "" ? undefined : value)
    .max(100, "Payment provider must be less than 100 characters"),
  trial_ends_at: yup.string().nullable().optional(),
  subscription_ends_at: yup.string().nullable().optional(),
});

interface OrganizationFormData {
  name: string;
  status: OrganizationStatus;
  payment_provider?: string;
  trial_ends_at?: string | null;
  subscription_ends_at?: string | null;
}

interface OrganizationCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of organization to fetch (for hash modal edit mode)
  onSuccess?: () => void;
}

// Helper function to get date 15 days from now
const getTrialEndDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 15);
  return date.toISOString();
};

const OrganizationCreateEditModal: React.FC<
  OrganizationCreateEditModalProps
> = ({ 
  isOpen, 
  onClose, 
  entityId,
  onSuccess 
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { 
    data: fetchedOrganization, 
    isLoading: isFetchingOrg, 
    error: fetchError 
  } = useGetOrganizationQuery(entityId!, {
    skip: !entityId // Only fetch when we have an entityId
  });

  // Use fetched data if available
  const editingEntity = fetchedOrganization;
  const { data: paymentProviders, isLoading: isLoadingPaymentProviders } =
    useGetPaymentProvidersQuery();

  // Memoize payment provider options
  const paymentProviderOptions = useMemo(() => {
    if (!paymentProviders) {
      return [{ value: "", label: "No Payment Provider" }];
    }

    const sortedProviders = [...paymentProviders].sort((a, b) =>
      a.display_name.localeCompare(b.display_name)
    );

    return [
      { value: "", label: "No Payment Provider" },
      ...sortedProviders.map((provider) => ({
        value: provider.name,
        label: provider.display_name,
      })),
    ];
  }, [paymentProviders]);
  const {
    handleSubmit,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<OrganizationFormData>({
    // resolver: yupResolver(organizationSchema), // Temporarily disabled for TypeScript compatibility
    defaultValues: {
      name: "",
      status: OrganizationStatus.TRIAL,
      payment_provider: undefined,
      trial_ends_at: getTrialEndDate(),
      subscription_ends_at: "",
    },
  });

  const { withErrorHandling, clearAllErrors } = useFormErrorHandler(
    setError,
    clearErrors
  );

  const [createOrganization, { isLoading: isCreating }] =
    useCreateOrganizationMutation();
  const [updateOrganization, { isLoading: isUpdating }] =
    useUpdateOrganizationMutation();

  const isLoading = isCreating || isUpdating || isSubmitting || isFetchingOrg;

  // Reset form when modal opens or editingEntity changes
  useEffect(() => {
    if (isOpen) {
      if (editingEntity) {
        reset({
          name: editingEntity.name,
          status: editingEntity.status,
          payment_provider: editingEntity.payment_provider?.name || "",
          trial_ends_at: editingEntity.trial_ends_at || "",
          subscription_ends_at: editingEntity.subscription_ends_at || "",
        });
      } else {
        reset({
          name: "",
          status: OrganizationStatus.TRIAL,
          payment_provider: "",
          trial_ends_at: getTrialEndDate(),
          subscription_ends_at: "",
        });
      }
      // Clear any previous server errors
      clearAllErrors();
    }
  }, [isOpen, editingEntity, reset, clearAllErrors]);

  const onSubmit = async (data: OrganizationFormData) => {
    await withErrorHandling(async () => {
      const payload = {
        name: data.name,
        status: data.status,
        ...(data.payment_provider && {
          payment_provider: data.payment_provider,
        }),
        ...(data.trial_ends_at && { trial_ends_at: data.trial_ends_at }),
        ...(data.subscription_ends_at && {
          subscription_ends_at: data.subscription_ends_at,
        }),
      };

      if (editingEntity) {
        await updateOrganization({
          id: editingEntity.id,
          data: payload,
        }).unwrap();
      } else {
        await createOrganization(payload).unwrap();
      }

      onSuccess?.();
      onClose();
    });
  };

  const handleClose = () => {
    reset({
      name: "",
      status: OrganizationStatus.TRIAL,
      payment_provider: "",
      trial_ends_at: getTrialEndDate(),
      subscription_ends_at: "",
    });
    clearAllErrors();
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle
          color="brand.700"
          _dark={{ color: "brand.300" }}
          fontSize="md"
          fontWeight="semibold"
        >
          {editingEntity
            ? "Edit Organization"
            : "Create New Organization"}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        {fetchError ? (
          <Text color="red.500" textAlign="center" py={4}>
            Error loading organization data. Please try again.
          </Text>
        ) : isFetchingOrg ? (
          <Text textAlign="center" py={4}>Loading organization data...</Text>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} id="organization-form">
            <VStack gap={4} align="stretch">
              {/* Display root/general errors using component */}
              <RootErrorDisplay error={errors.root} />

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Organization Name
              </Text>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter organization name"
                    bg="white"
                    _dark={{ bg: "gray.700" }}
                    borderColor={errors.name ? "red.500" : undefined}
                  />
                )}
              />
              <FormErrorDisplay error={errors.name} />
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Status
              </Text>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={ORGANIZATION_STATUS_OPTIONS}
                    placeholder="Select status"
                  />
                )}
              />
              <FormErrorDisplay error={errors.status} />
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Payment Provider
              </Text>
              <Controller
                name="payment_provider"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={paymentProviderOptions}
                    placeholder="Select a payment provider"
                    disabled={isLoadingPaymentProviders}
                  />
                )}
              />
              <FormErrorDisplay error={errors.payment_provider} />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Optional - Payment provider for this organization
              </Text>
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Trial Ends At
              </Text>
              <Controller
                name="trial_ends_at"
                control={control}
                render={({ field }) => (
                  <DateInput
                    {...field}
                    placeholder="Select trial end date"
                    borderColor={errors.trial_ends_at ? "red.500" : undefined}
                  />
                )}
              />
              <FormErrorDisplay error={errors.trial_ends_at} />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Optional - When the trial period ends
              </Text>
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                Subscription Ends At
              </Text>
              <Controller
                name="subscription_ends_at"
                control={control}
                render={({ field }) => (
                  <DateInput
                    {...field}
                    placeholder="Select subscription end date"
                    borderColor={
                      errors.subscription_ends_at ? "red.500" : undefined
                    }
                  />
                )}
              />
              <FormErrorDisplay error={errors.subscription_ends_at} />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Optional - When the subscription expires
              </Text>
            </Box>
          </VStack>
        </form>
        )}
      </DrawerBody>

      <DrawerFooter>
        <HStack justify="end" gap={3}>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {!fetchError && !isFetchingOrg && (
            <Button
              type="submit"
              form="organization-form"
              colorPalette="brand"
              loading={isLoading}
              disabled={isLoading}
            >
              {editingEntity ? "Update" : "Create"}
            </Button>
          )}
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const OrganizationCreateModal = withHashDrawer(OrganizationCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const OrganizationEditModal = withHashDrawer(OrganizationCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default OrganizationCreateEditModal;
